import { FontAwesomeIcons } from "../../../../core/Types"


// Notification settings live on the user (User.notificationsSettings): everyone decides
// for themselves what reaches them and how. New notification types are added by
// extending NotificationKey + NotificationSections (and the matching model on the
// backend).

export type NotificationKey = "chat"

// The per-type options, each rendered as a compact chip under its section.
export type NotificationOptionKey = "push" | "inApp" | "playSound" | "keepOnScreen"

export type NotificationTypeSettings = {
    // Receive this notification at all. Turning it off hides every option below.
    enabled: boolean
} & {
    [key in NotificationOptionKey]: boolean
}

export type NotificationsSettingsModel = {
    [key in NotificationKey]: NotificationTypeSettings
}

export interface NotificationOption {
    key: NotificationOptionKey
    name: string
    hint: string
    icon: FontAwesomeIcons
    // Only offered while this other option is on, and switched off with it. Sound and
    // staying on screen describe the in-app banner, so without it they mean nothing.
    requires?: NotificationOptionKey
}

export interface NotificationSection {
    key: NotificationKey
    name: string
    description: string
    icon: FontAwesomeIcons
    color: string
    defaults: NotificationTypeSettings
}


export const NotificationOptions: NotificationOption[] = [
    {
        key: "push",
        name: "Push Notification",
        // Delivered by the device (browser / mobile app), so it also arrives while
        // the app is closed or sitting in the background.
        hint: "Shown by the device even when the app is closed or in the background.",
        icon: "bell-on"
    },
    {
        key: "inApp",
        name: "In-App Notification",
        hint: "Shown inside the app while it is open and in use.",
        icon: "window-maximize"
    },
    {
        key: "playSound",
        name: "Play Sound",
        hint: "Play a sound when the in-app notification appears.",
        icon: "volume-high",
        requires: "inApp"
    },
    {
        key: "keepOnScreen",
        name: "Keep on Screen",
        hint: "Keep the in-app notification on screen until it is closed, otherwise it closes automatically.",
        icon: "thumbtack",
        requires: "inApp"
    }
]


export const NotificationSections: NotificationSection[] = [
    {
        key: "chat",
        name: "Chat Messages",
        description: "Get notified when a new chat message is received.",
        icon: "comments",
        color: "#34c759",
        // On out of the box, but only as a push notification: the in-app banner,
        // the sound and keep-on-screen stay off until the admin asks for them.
        defaults: {
            enabled: true,
            push: true,
            inApp: false,
            playSound: false,
            keepOnScreen: false
        }
    }
]


export function defaultNotificationsSettings(): NotificationsSettingsModel {
    const result = {} as NotificationsSettingsModel
    for (const section of NotificationSections) {
        result[section.key] = { ...section.defaults }
    }
    return result
}


// Accounts saved before a notification type (or one of its options) existed have no
// value for it, so fall back to that type's defaults instead of showing it as off.
export function normalizeNotificationsSettings(data?: Partial<NotificationsSettingsModel>): NotificationsSettingsModel {
    const result = defaultNotificationsSettings()
    if (!data) return result
    for (const section of NotificationSections) {
        const saved = data[section.key]
        if (saved) result[section.key] = { ...section.defaults, ...saved }
    }
    return result
}
